import { Component, ReactNode } from "react";
import { Button, Group, Stack, Text, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { t } from "i18next";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// A React error boundary has to be a class component - there's no hook
// equivalent. Sits once at the very top of the tree (see index.tsx),
// outside the router, so an uncaught render error anywhere - a malformed
// Firestore doc missing a field this session's own bugs have hit before, a
// third-party library quirk, anything - shows a recoverable message
// instead of silently unmounting the whole app to a blank white screen.
// The fallback is deliberately self-contained (no router hooks, no
// translation hook, plain Mantine components) so it can't itself fail for
// the same reason the error it's catching did.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("Uncaught render error:", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <Stack align="center" ta="center" gap="md" py="20vh" px="md" maw={420} mx="auto">
        <IconAlertTriangle size={48} stroke={1.5} color="var(--mantine-color-red-6)" />
        <Title order={2}>{t("error.boundary.title")}</Title>
        <Text c="dimmed">{t("error.boundary.description")}</Text>
        <Group>
          <Button variant="default" onClick={() => (window.location.href = "/")}>
            {t("error.boundary.home")}
          </Button>
          <Button onClick={() => window.location.reload()}>{t("error.boundary.reload")}</Button>
        </Group>
      </Stack>
    );
  }
}
